<template>
  <div
    v-if="toggle"
    class="rs-banner"
    :class="`${customBorderColor === '' ? `border-${type}` : ''} ${customBgColor === '' ? ` bg-color-${type}` : ''} ${customBorderColor === '' ? ` cap-${type}` : ''}`"
    :style="[bannerBgColor, borderAndLeftCapColor]"
  >
    <div
      class="d-flex"
      :style="bannerHeight"
    >
      <div class="d-flex align-items-center justify-content-between py-1 w-100 default-text-pl default-banner-pr">
        <div
          class="d-flex"
          :class="`${ isTitleMessageStackedWithPlainBtn ? 'align-items-end' : 'align-items-center'
          }`"
        >
          <RsIcon
            v-if="!noIcon && !iconImg"
            class="rs-banner-icon"
            :class="`${customIconColor === '' ? `icon-color-${type}` : ''}`"
            :style="[iconFontSize, iconColor]"
            :icon="iconName"
            :weight="iconWeight"
          />

          <img
            v-if="iconImg"
            class="d-inline rs-banner-icon"
            :src="iconImg"
            :style="imgSizeStyle"
          />

          <div
            class="d-flex"
            :class="{
              'flex-column justify-content-center': isTitleMessageStacked,
              'flex-row align-items-baseline': !isTitleMessageStacked,
              'default-text-pl': !noIcon,
            }"
          >
            <div
              v-if="title"
              class="title font-weight-bold"
              :class="{ 'pb-1': isTitleMessageStacked, 'rs-banner-text-pr-sm': !isTitleMessageStacked && isSmall, 'rs-banner-text': isLarge || isCustom }"
              :style="titleFontSize"
              v-html="title"
            />
            <div
              v-if="message"
              class="text flex-grow-1 rs-banner-text text-justify"
              :style="messageFontStyle"
              v-html="$utils.decodeHtml(message || '')"
            />
            <div
              v-if="!message"
              class="py-1"
            >
              <slot
                name="message"
              />
            </div>
          </div>
          <RouterLink
            v-if="hasActionBtn && isActionBtnPlain && !isPlainActionBtnRight"
            to="#"
            :class="{ 'lh-1': isTitleMessageStackedWithPlainBtn }"
            @click="handleAction"
          >
            <span
              class="default-text-size font-weight-bold"
              :style="btnTextColor"
            >{{ actionButtonText }}</span>
          </RouterLink>
        </div>
        <RsButton
          v-if="hasActionBtn && !isActionBtnPlain"
          :class="`${customBtnColor === '' && !isActionBtnPlain && !isActionBtnDisabled ? `btn-color-${type}` : 'btn-color-disabled'}`"
          :style="[btnBgColor, btnTextColor, btnWidth]"
          :disabled="isActionBtnDisabled"
          :plain="isActionBtnPlain"
          @click="handleAction"
        >
          {{ actionButtonText }}
        </RsButton>
        <RouterLink
          v-if="hasActionBtn && isActionBtnPlain && isPlainActionBtnRight"
          to="#"
          :class="{ 'lh-1': isTitleMessageStackedWithPlainBtn }"
          @click="handleAction"
        >
          <span
            class="default-text-size font-weight-bold"
            :style="btnTextColor"
          >{{ actionButtonText }}</span>
        </RouterLink>
      </div>
      <div
        class="d-flex"
        :class="{ 'align-items-start pt-2': isLarge || (isCustom && isCustomHeightLarge), 'align-items-center padding-top-2': isSmall || (isCustom && !isCustomHeightLarge) }"
      >
        <button
          v-if="isDismissible"
          class="close banner-close-right"
          @click="handleClose"
        >
          <RsIcon
            class="fa-sm"
            icon="times"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

const DEFAULT_HEIGHT = 55;
const DEFAULT_ICON_SIZE = 20;
const DEFAULT_TEXT_SIZE = 14;

const iconSizeMap = {
  small: 16,
  large: DEFAULT_ICON_SIZE,
} as Record<string, number>;

const iconNameMap = {
  success: 'check-circle',
  warning: 'exclamation-triangle',
  error: 'circle-exclamation',
  'help-text-primary': 'question-circle',
} as Record<string, string>;

const heightMap = {
  small: 32,
  large: DEFAULT_HEIGHT,
} as Record<string, number>;

export default defineComponent({
  name: 'RsBanner',

  props: {
    /**
     * This will determine the height and icon size of the banner.
     * Small and large options are configured in 'iconSizeMap' and 'heightMap' variables.
     * Choose custom to supply non-configured values for 'customHeight' and 'customIconSize' props.
     */
    size: {
      type: String as PropType<'small'|'large'|'custom'>,
      default: 'large',
    },
    /**
     * Variants configured as per doc. Configuration includes same color for border,
     * left cap, and button, has default icon, and has default background color.
     * Refer to doc below when adding a type.
     * @values - 'success | warning | error | help-text-primary'
     * @see https://zeroheight.com/82d960aef/p/34d254-banner/b/21bb26
     */
    type: {
      type: String as PropType<'success' | 'warning' | 'error' | 'help-text-primary'>,
      default: '',
    },
    /**
     * Title of the banner
     */
    title: {
      type: String,
      default: '',
    },
    /**
     * Font size for the title
     */
    titleSize: {
      type: Number,
      default: DEFAULT_TEXT_SIZE,
    },
    /**
     * Main text of the banner. It could be html
     */
    message: {
      type: String,
      default: '',
    },
    /**
     * Text shown on the button
     */
    actionButtonText: {
      type: String,
      default: 'Action',
    },
    /**
     * Left icon name
     */
    icon: {
      type: String,
      default: '',
    },
    /**
     * Fontawesome icon weight
     * @values - far | fas | fal | fad
     */
    iconWeight: {
      type: String,
      default: 'fas',
    },
    /**
     * Boolean to indicate whether to show or hide left icon
     */
    noIcon: {
      type: Boolean,
      default: false,
    },
    /**
     * Font size for main message text
     */
    textSize: {
      type: Number,
      default: DEFAULT_TEXT_SIZE,
    },
    /**
     * Font weight for main message text
     */
    textWeight: {
      type: Number,
      default: 400,
    },
    /**
     * Boolean to indicate whether to show or hide the close button
     */
    isDismissible: {
      type: Boolean,
      default: false,
    },
    /**
     * Boolean to indicate whether to show or hide the action button
     */
    hasActionBtn: {
      type: Boolean,
      default: false,
    },
    /**
     * Boolean to indicate whether button is displayed as plain text
     */
    isActionBtnPlain: {
      type: Boolean,
      default: false,
    },
    /**
     * Boolean to indicate whether the action link is displayed to the rightmost end of the banner
     */
    isPlainActionBtnRight: {
      type: Boolean,
      default: false,
    },
    /**
     * Boolean to enable/disable action btn
     */
    isActionBtnDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Boolean to indicate whether title and message are stacked
     */
    isTitleMessageStacked: {
      type: Boolean,
      default: false,
    },
    /**
     * Custom height of the banner.
     * Use if 'custom' is selected for the prop 'size'.
     * Use together with 'customIconSize'
     */
    customHeight: {
      type: Number,
      default: DEFAULT_HEIGHT,
    },
    /**
     * Custom icon size for banner.
     * Use if 'custom' is selected for the prop 'size'.
     * Use together with 'customHeight'
     */
    customIconSize: {
      type: Number,
      default: DEFAULT_ICON_SIZE,
    },
    /**
     * Custom color for icon in hex NOT prepended with '#'.
     * Use when icon color is not already configured based on the prop 'type'.
     * and is a one-time individual case. Otherwise, refer to doc below and add
     * configuration
     * @see https://zeroheight.com/82d960aef/p/34d254-banner/b/21bb26
     */
    customIconColor: {
      type: String,
      default: '',
    },
    /**
     * Custom color for banner border in hex NOT prepended with '#'.
     * Use when banner border color is not already configured based on the prop 'type'
     * and is a one-time individual case. Otherwise, refer to doc below and add
     * configuration
     * @see https://zeroheight.com/82d960aef/p/34d254-banner/b/21bb26
     */
    customBorderColor: {
      type: String,
      default: '',
    },
    /**
     * Custom color for banner background in hex NOT prepended with '#'.
     * Use when background color is not already configured based on the prop 'type'
     * and is a one-time individual case. Otherwise, refer to doc below and add
     * configuration
     * @see https://zeroheight.com/82d960aef/p/34d254-banner/b/21bb26
     */
    customBgColor: {
      type: String,
      default: '',
    },
    /**
     * Custom color for button background color in hex NOT prepended with '#'.
     * Use when button background color is not already configured based on the prop 'type'
     * and is a one-time individual case. Otherwise, refer to doc below and add
     * configuration
     * @see https://zeroheight.com/82d960aef/p/34d254-banner/b/21bb26
     */
    customBtnColor: {
      type: String,
      default: '',
    },
    /**
     * Custom color for button text color in hex NOT prepended with '#'.
     */
    customBtnTextColor: {
      type: String,
      default: '221A40',
    },
    /**
     * Custom width for the button.
     * Use for long button texts that should not wrap
     * and where button width may become too small
     * i.e. when resizing
     */
    customBtnWidth: {
      type: Number,
      default: null,
    },

    /**
     * An image can be rendered for the icon slot instead of an icon.
     * Note that using `icon` and `iconImg` props together will display
     * both image and icon.
     */
    iconImg: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      toggle: true as boolean,
    };
  },

  computed: {
    isSmall(): boolean {
      return this.size === 'small';
    },

    isLarge(): boolean {
      return this.size === 'large';
    },

    isCustom(): boolean {
      return this.size === 'custom';
    },

    isCustomHeightLarge(): boolean {
      return this.customHeight > DEFAULT_HEIGHT;
    },

    iconFontSize(): Record<string, string> {
      let size: number = 0;
      if (this.isSmall || this.isLarge) size = iconSizeMap[`${this.size}`];
      if (this.isCustom) size = this.customIconSize;
      return {
        'font-size': `${size}px`,
      };
    },

    /**
     * If image icon is being used, adjusts its size using width instead of
     * the regular icon's font-size style attribute.
     */
    imgSizeStyle(): string {
      if (this.isSmall || this.isLarge) {
        return `width:${iconSizeMap[`${this.size}`]}`;
      }

      if (this.customIconSize) {
        return `width:${this.customIconSize}px`;
      }

      return '';
    },

    iconColor(): Record<string, string> {
      return {
        color: `#${this.customIconColor}`,
      };
    },

    iconName(): string {
      return !this.icon ? iconNameMap[this.type] : this.icon;
    },

    bannerHeight(): Record<string, string> {
      let bannerHeight: number = 0;
      if (this.isSmall || this.isLarge) bannerHeight = heightMap[`${this.size}`];
      if (this.isCustom) bannerHeight = this.customHeight;
      return {
        'min-height': `${bannerHeight}px`,
        'min-width': '100%',
      };
    },

    bannerBgColor(): Record<string, string> {
      return {
        'background-color': `#${this.customBgColor}`,
      };
    },

    btnBgColor(): Record<string, string> {
      return {
        'background-color': `${this.isActionBtnDisabled && !this.type ? '#c9ccdf' : `#${this.customBtnColor}`}`,
        border: 'none',
      };
    },

    btnTextColor(): Record<string, string> {
      return {
        color: `${this.isActionBtnDisabled && !this.type ? '#3D4756' : `#${this.customBtnTextColor}`}`,
      };
    },

    btnWidth() : Record<string, string> {
      return {
        'min-width': `${this.customBtnWidth}px`,
      };
    },

    borderAndLeftCapColor(): Record<string, string> {
      return {
        border: `1px solid #${this.customBorderColor}`,
        'box-shadow': `4px #${this.customBorderColor}`,
        'border-left': `15px solid #${this.customBorderColor}`,
      };
    },

    titleFontSize(): Record<string, string> {
      return { 'font-size': `${this.titleSize}px` };
    },

    messageFontStyle(): Record<string, string> {
      return { 'font-size': `${this.textSize}px`, 'font-weight': `${this.textWeight}` };
    },

    isTitleMessageStackedWithPlainBtn(): boolean {
      return this.isTitleMessageStacked && this.hasActionBtn && this.isActionBtnPlain;
    },
  },

  methods: {
    handleAction():void {
      this.$emit('on-action');
    },
    handleClose():void {
      this.$emit('on-banner-close');
      this.toggle = false;
    },
  },
});

</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

$banner-success: #37955B;
$banner-success-light: #DCEFE3;
$banner-warning-icon: #B87A00;
$banner-warning-light: #FFEBCC;

.rs-banner{
  box-sizing: border-box;
  border-radius: $input-radius;
  font-size: 0.75rem;
  max-height: 100%;
  margin-left: 0px;
  margin-right: 5px;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  &-text {
    padding-right: 19px;
  }
   &-text-pr-sm {
    padding-right: 9px;
  }
}
.banner {
  &-close-right {
    padding: 0px 13px 0px 0px;
  }
}
.text {
  color: $secondary-color-dark;
  line-height:1;
}
.title {
  color: $secondary-color-dark;
  line-height:1;
  font-weight:700;
}

.default-text-size {
  font-size: 14px;
}

.default-text-pl {
  padding-left: 19px;
}

.default-banner-pr {
  padding-right: 13px;
}

.padding-top-2 {
  padding-top: 2px;
}
/******Banner border config*****/
.border-success {
  border: 1px solid $banner-success;
  box-shadow: 4px $banner-success-light;
}
.border-help-text-primary {
  border: 1px solid $secondary-color;
  box-shadow: 4px $secondary-color;
}

.border-warning{
  border: 1px solid $warning-icon;
  box-shadow: 4px $warning;
}
.border-error{
  border: 1px solid $error;
  box-shadow: 4px $error;
}

/******Left cap config*****/
.cap-success {
  border-left: 15px solid $banner-success;
}
.cap-help-text-primary {
  border-left: 15px solid $secondary-color;
}
.cap-warning {
  border-left: 15px solid $warning-icon;
}
.cap-error {
  border-left: 15px solid $error;
}

/******Icon color config*****/
.icon-color-success{
  color: $banner-success;
}
.icon-color-help-text-primary{
  color: $secondary-color;
}

.icon-color-warning{
  color: $banner-warning-icon;
}
.icon-color-error{
  color: $error;
}

/******Button background color config*****/
.btn-color-success{
  background-color: $banner-success;
}
.btn-color-success:hover{
  background-color: $banner-success;
  border-color: $banner-success;
}
.btn-color-help-text-primary{
  background-color: $secondary-color;
}
.btn-color-help-text-primary:hover{
  background-color: $secondary-color;
  border-color: $secondary-color;
}

.btn-color-warning{
  background-color: $warning;
}
.btn-color-warning:hover{
  background-color: $warning;
  border-color: $warning;
}
.btn-color-error{
  background-color: $error;
}
.btn-color-error:hover{
  background-color: $error;
  border-color: $error;
}

.btn-color-disabled {
  background-color: #c9ccdf;
  color: #3D4756;
  border: none;
}

/*********Banner background config*********/
.bg-color-success {
  background-color: $banner-success-light;
}
.bg-color-help-text-primary {
  background-color: $secondary-light;
}

.bg-color-warning {
  background-color: $warning-background;
}
.bg-color-error {
  background-color: $error-light;
}
</style>
